import { defineStore } from 'pinia';
import { ref, computed, watchEffect, onScopeDispose } from 'vue';

export type ThemeMode = 'light' | 'dark' | 'system';

export const THEME_MODE_STORAGE_KEY = 'theme-mode';
const DARK_QUERY = '(prefers-color-scheme: dark)';

// Resolution logic is mirrored by the inline pre-paint guard in index.html.
// Keep the two in sync: any change to THEME_MODE_STORAGE_KEY / accepted modes / the dark
// rule below must be reflected there as well.
function readStoredMode(): ThemeMode {
    try {
        const stored = localStorage.getItem(THEME_MODE_STORAGE_KEY);
        if (stored === 'light' || stored === 'dark' || stored === 'system') {
            return stored;
        }
    } catch {
        // localStorage may be unavailable (e.g. private mode); fall through to default.
    }
    return 'system';
}

export const useThemeStore = defineStore('theme', () => {
    const mode = ref<ThemeMode>(readStoredMode());

    const mediaQueryList = window.matchMedia(DARK_QUERY);
    const systemPrefersDark = ref<boolean>(mediaQueryList.matches);

    const onSystemChange = (event: MediaQueryListEvent) => {
        systemPrefersDark.value = event.matches;
    };
    mediaQueryList.addEventListener('change', onSystemChange);
    // The store lives for the app's lifetime in production, so onScopeDispose is
    // effectively a no-op there. It exists for HMR and unit-test cleanliness.
    onScopeDispose(() => mediaQueryList.removeEventListener('change', onSystemChange));

    const resolvedTheme = computed<'light' | 'dark'>(() =>
        mode.value === 'system' ? (systemPrefersDark.value ? 'dark' : 'light') : mode.value,
    );

    watchEffect(() => {
        try {
            localStorage.setItem(THEME_MODE_STORAGE_KEY, mode.value);
        } catch {
            // Ignore quota / disabled-storage errors; runtime toggle below still works.
        }
        const root = document.documentElement;
        if (resolvedTheme.value === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
    });

    function setMode(next: ThemeMode) {
        mode.value = next;
    }

    return { mode, resolvedTheme, setMode };
});
